import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';

type ExtractionSchema = Record<string, unknown>;
type Prompt = Record<string, unknown>;

export const promptsSchemaApi = createApi({
  reducerPath: 'promptsSchemaApi',
  baseQuery: customBaseQuery,
  tagTypes: ['ExtractionSchema', 'Prompt'],
  endpoints: (builder) => ({
    getExtractionSchemas: builder.query<ApiResponse<ExtractionSchema[]>, void>({
      query: () => '/v1/prompts/extraction-schemas',
      providesTags: ['ExtractionSchema'],
    }),

    getExtractionSchemaByKey: builder.query<
      ApiResponse<ExtractionSchema>,
      string
    >({
      query: (key) => `/v1/prompts/extraction-schemas/${key}`,
      providesTags: (_result, _err, key) => [
        { type: 'ExtractionSchema', id: key },
      ],
    }),

    upsertExtractionSchema: builder.mutation<
      ApiResponse<ExtractionSchema>,
      { key: string } & Record<string, unknown>
    >({
      query: (body) => ({
        url: '/v1/prompts/extraction-schemas',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ExtractionSchema'],
    }),

    createPrompt: builder.mutation<
      ApiResponse<Prompt>,
      Record<string, unknown>
    >({
      query: (body) => ({
        url: '/v1/prompts',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Prompt'],
    }),

    getPromptByDomainAndKey: builder.query<
      ApiResponse<Prompt>,
      { domain: string; key: string }
    >({
      query: ({ domain, key }) => `/v1/prompts/${domain}/${key}`,
      providesTags: (_result, _err, { domain, key }) => [
        { type: 'Prompt', id: `${domain}/${key}` },
      ],
    }),

    updatePrompt: builder.mutation<
      ApiResponse<Prompt>,
      { domain: string; key: string; body: Record<string, unknown> }
    >({
      query: ({ domain, key, body }) => ({
        url: `/v1/prompts/${domain}/${key}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Prompt'],
    }),

    deletePrompt: builder.mutation<
      ApiResponse<void>,
      { domain: string; key: string }
    >({
      query: ({ domain, key }) => ({
        url: `/v1/prompts/${domain}/${key}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Prompt'],
    }),

    deleteExtractionSchema: builder.mutation<ApiResponse<void>, string>({
      query: (key) => ({
        url: `/v1/prompts/extraction-schemas/${key}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ExtractionSchema'],
    }),

    getUniqueFields: builder.query<ApiResponse<Record<string, string[]>>, void>(
      {
        query: () => '/v1/prompts/unique-fields',
      },
    ),
  }),
});

export const {
  useGetExtractionSchemasQuery,
  useGetExtractionSchemaByKeyQuery,
  useUpsertExtractionSchemaMutation,
  useCreatePromptMutation,
  useGetPromptByDomainAndKeyQuery,
  useUpdatePromptMutation,
  useDeletePromptMutation,
  useDeleteExtractionSchemaMutation,
  useGetUniqueFieldsQuery,
} = promptsSchemaApi;
